// src/components/AuditLogs.jsx
import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "services/supabaseClient";
import DateRangeSelector from "components/DateRangeSelector";
import SectionCard from "components/SectionCard";
import exportToExcel from "utils/exportExcel";
import dayjs from "lib/dayjs";
import { useDialog } from "context/DialogContext";

export default function AuditLogs() {
  const { alert } = useDialog();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ dateFrom: "", dateTo: "" });

  const fetchLogs = useCallback(async () => {
    setLoading(true);

    let query = supabase
      .from("v_audit_logs_with_actors")
      .select(
        "id, category, action, success, detail, created_at, actor_first_name, actor_last_name, actor_email"
      )
      .order("created_at", { ascending: false });

    if (filters.dateFrom) {
      query = query.gte("created_at", filters.dateFrom + "T00:00:00Z");
    }
    if (filters.dateTo) {
      query = query.lte("created_at", filters.dateTo + "T23:59:59Z");
    }

    const { data, error } = await query;
    if (error) {
      await alert("Failed to fetch audit logs: " + error.message, {
        title: "Audit logs",
        okLabel: "OK",
      });
    } else {
      setLogs(data || []);
    }
    setLoading(false);
  }, [filters.dateFrom, filters.dateTo, alert]);

  const handleDateRangeChange = (dateFrom, dateTo) => {
    setFilters({ dateFrom, dateTo });
  };

  const handleExport = () => {
    exportToExcel({
      sheetName: "AuditLogs",
      fileName: `AuditLogs_${dayjs().format("YYYY-MM-DD")}.xlsx`,
      headers: [
        "Timestamp",
        "Category",
        "Action",
        "Success",
        "Actor Name",
        "Actor Email",
        "Detail",
      ],
      rows: logs.map((log) => ({
        Timestamp: new Date(log.created_at),
        Category: log.category,
        Action: log.action,
        Success: log.success === true ? "✔" : log.success === false ? "✖" : "",
        "Actor Name": `${log.actor_first_name ?? ""} ${
          log.actor_last_name ?? ""
        }`.trim(),
        "Actor Email": log.actor_email,
        Detail: log.detail ? JSON.stringify(log.detail) : "",
      })),
      title: "Audit Logs Export",
      subtitle: `As of ${dayjs().format("M/D/YYYY")}`,
    });
  };

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <SectionCard>
      <div className="mb-3 text-lg font-semibold text-app">Audit Logs</div>

      {/* Date filters + actions */}
      <div className="flex flex-wrap gap-3 mb-4 text-sm">
        <DateRangeSelector
          defaultRange="30"
          onApplyFilters={handleDateRangeChange}
        />

        <button
          onClick={handleExport}
          className="btn btn-primary"
          disabled={logs.length === 0}
        >
          Export to Excel
        </button>
      </div>

      {/* Logs table */}
      {loading ? (
        <div className="text-sm text-muted">Loading logs...</div>
      ) : logs.length === 0 ? (
        <div className="text-sm text-muted italic">No audit logs found.</div>
      ) : (
        <div className="overflow-auto rounded-xl border border-app">
          <table className="table-auto w-full text-sm">
            <thead className="bg-app/50">
              <tr className="text-left text-muted">
                <th className="py-2 px-3">Timestamp</th>
                <th className="px-3">Name</th>
                <th className="px-3">Email/Username</th>
                <th className="px-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr
                  key={log.id}
                  className="border-t border-app hover:bg-app/50"
                >
                  <td className="py-2 px-3 whitespace-nowrap">
                    {new Date(log.created_at).toLocaleString()}
                  </td>
                  <td className="px-3">
                    {log.actor_last_name}, {log.actor_first_name}
                  </td>
                  <td className="px-3">{log.actor_email}</td>
                  <td className="px-3">
                    {log.category}:{log.action}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </SectionCard>
  );
}
